import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import { UseProspectSetupFee } from "../../../../hooks/useProspectSetupFee";
import { SetupFee } from "../../../../types/onboarding";
import { NextStepAction } from "../../../../utils/prospectNextStep";
import { actorLabel } from "../../../../utils/actors";
import { formatInstant } from "../../../../utils/instantFormat";
import {
  formatSetupFeeAmount,
  setupFeeStatusLabel,
} from "../../../../utils/setupFee";
import SetupFeeDialog from "./SetupFeeDialog";

interface SetupFeeCardProps {
  setupFee: UseProspectSetupFee;
  requestedAction?: NextStepAction | null;
  onRequestHandled?: () => void;
}

const decision = (fee: SetupFee): string =>
  [actorLabel(fee.decided_by), formatInstant(fee.decided_at)]
    .filter((part) => part && part !== "—")
    .join(" · ");

/**
 * The onboarding setup fee (CWA-196) under the station configuration. Not a
 * stage: nothing here blocks the configuration email or go-live (FR-010).
 * Hidden until the fee falls due at promotion.
 */
const SetupFeeCard: React.FC<SetupFeeCardProps> = ({
  setupFee: hook,
  requestedAction = null,
  onRequestHandled,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const fee = hook.setupFee;

  useEffect(() => {
    if (requestedAction !== "setup-fee") return;
    setDialogOpen(true);
    onRequestHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedAction]);

  if (hook.error) {
    return (
      <Message
        severity="warn"
        className="w-full mb-4"
        text="The setup fee could not be loaded. Reload the page to try again."
      />
    );
  }
  if (!fee || fee.status === "NOT_DUE") return null;

  const amount = `${formatSetupFeeAmount(fee.gross_amount_cents)} brutto`;

  return (
    <div className="mb-4 onboarding-section" data-testid="setup-fee-card">
      <div className="flex flex-wrap align-items-center gap-2 mb-2">
        <h3 className="m-0">Setup fee</h3>
        <Tag
          value={setupFeeStatusLabel(fee.status)}
          severity={fee.status === "OPEN" || fee.status === "INVOICING" ? "warning" : "info"}
        />
      </div>

      {fee.status === "OPEN" && (
        <p className="mt-0">Not invoiced yet: {amount}.</p>
      )}
      {fee.status === "INVOICING" && (
        <p className="mt-0">
          An invoice request for {amount} has not come back from billing yet.
        </p>
      )}
      {fee.status === "INVOICED" && (
        <p className="mt-0">
          Draft invoice {fee.invoice?.title} for {amount} created in Easybill
          {decision(fee) && ` (${decision(fee)})`}. Finance finalises and sends it
          there.{" "}
          {fee.invoice?.url && (
            <a href={fee.invoice.url} target="_blank" rel="noopener noreferrer">
              Open draft in Easybill <i className="pi pi-external-link text-xs" />
            </a>
          )}
        </p>
      )}
      {fee.status === "WAIVED" && (
        <p className="mt-0">
          Waived{decision(fee) && ` by ${decision(fee)}`}. Reason:{" "}
          {fee.waiver_reason}
        </p>
      )}

      {fee.actions_available && (
        <Button
          label={fee.status === "INVOICING" ? "Retry invoice" : "Handle setup fee"}
          icon="pi pi-euro"
          outlined
          onClick={() => setDialogOpen(true)}
        />
      )}

      <SetupFeeDialog
        visible={dialogOpen}
        setupFee={fee}
        onHide={() => setDialogOpen(false)}
        onInvoice={hook.invoice}
        onWaive={hook.waive}
      />
    </div>
  );
};

export default SetupFeeCard;
